import * as React from 'react';
import { AppBar, Toolbar, IconButton, Typography, WithStyles, Theme, Drawer, Divider, ListItemIcon, ListItemText, Button, Grid, MenuList, MenuItem } from '@material-ui/core';
import ListIcon from '@material-ui/icons/List';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import withStyles, { CSSProperties } from '@material-ui/core/styles/withStyles';
import * as classNames from 'classnames';
import { MenuItemType } from '../../model/menuItems';
import { bindActionCreators, ActionCreatorsMapObject } from 'redux';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../store';
import { changed, ChangedAction } from '../../actions/menuItemActions';
import { AccountLogoutActionCreator, AccountLogoutAsyncActionCreator, accountLogoutAsync } from '../../actions';
import ContinentsPage from '../../modules/continent/ContinentsPage';
import CountriesPage from '../../modules/country/CountriesPage';
import AppellationsPage from '../../modules/appellation/AppellationsPage';
import ClassificationsPage from '../../modules/classification/ClassificationsPage';
import RedVarietiesPage from '../../modules/redVariety/RedVarietiesPage';
import WhiteVarietiesPage from '../../modules/whiteVariety/WhiteVarietiesPage';
import SubregionsPage from '../../modules/subregion/SubregionsPage';
import RegionsPage from '../../modules/region/RegionsPage';
import WineriesPage from '../../modules/winery/WineriesPage';

//#region UI configuration
const strings = {
    title: '🍇 VinVoir',
    logOutButton: 'Log out',
    wineries: 'Wineries',
    appellations: 'Appellations',
    classifications: 'Classifications',
    redVarieties: 'Red Varieties',
    whiteVarieties: 'White Varieties',
    subregions: 'Subregions',
    regions: 'Regions',
    countries: 'Countries',
    continents: 'Continents'
};

const drawerWidth = 240;

const styles = (theme: Theme) => ({
    root: {
        flexGrow: 1,
        zIndex: 1,
        position: 'relative',
        display: 'flex',
        minHeight: '100%'
    } as CSSProperties,
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    } as CSSProperties,
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    } as CSSProperties,
    appBarToolbar: {
        paddingRight: 0
    } as CSSProperties,
    menuButton: {
        marginLeft: 12,
        marginRight: 36,
    } as CSSProperties,
    title: {
        flex: 1,
    },
    hide: {
        display: 'none',
    } as CSSProperties,
    drawerPaper: {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    } as CSSProperties,
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing.unit * 7,
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing.unit * 9,
        },
    } as CSSProperties,
    drawerToolbar: {
        height: 'auto',
        position: 'fixed',
        width: drawerWidth,
        background: '#fff',
        zIndex: 2,
        ...theme.mixins.toolbar,
    } as CSSProperties,
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        height: 'auto',
        ...theme.mixins.toolbar,
    } as CSSProperties,
    content: {
        minHeight: '100%',
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing.unit * 3,
    } as CSSProperties
});

type Styles = WithStyles<'root' | 'appBar' | 'appBarShift' | 'appBarToolbar' | 'menuButton' | 'title' | 'hide'
    | 'drawerPaper' | 'drawerPaperClose' | 'drawerToolbar' | 'toolbar' | 'content'>;
//#endregion

//#region Props & State
interface HomePageStateProps {
    readonly currentMenuItem: MenuItemType | null;
}
interface HomePageDispatchProps {
    readonly logout: () => Promise<void>;
    readonly changeMenuItem: (current: MenuItemType) => void;
}
interface HomePageProps extends HomePageStateProps, HomePageDispatchProps { }
interface HomePageState {
    open: boolean;
}

type PropsWithStyles = HomePageProps & Styles;

const initialState: HomePageState = {
    open: true
};
//#endregion

class HomePage extends React.Component<PropsWithStyles, HomePageState> {

    private readonly menuItems = [
        { id: MenuItemType.Wineries, title: strings.wineries },
        { id: MenuItemType.Appellations, title: strings.appellations },
        { id: MenuItemType.Classifications, title: strings.classifications },
        { id: MenuItemType.RedVarieties, title: strings.redVarieties },
        { id: MenuItemType.WhiteVarieties, title: strings.whiteVarieties },
        { id: MenuItemType.Subregions, title: strings.subregions },
        { id: MenuItemType.Regions, title: strings.regions },
        { id: MenuItemType.Countries, title: strings.countries },
        { id: MenuItemType.Continents, title: strings.continents }
    ];

    constructor(props: PropsWithStyles) {
        super(props);

        this.state = initialState;
    }

    render() {
        const { classes, currentMenuItem } = this.props;
        const { open } = this.state;

        return (
            <div className={classes.root}>
                <AppBar
                    position="fixed"
                    className={classNames(classes.appBar, open && classes.appBarShift)}
                >
                    <Toolbar disableGutters={!open} className={classes.appBarToolbar}>
                        <IconButton
                            color="inherit"
                            onClick={this.handleDrawerOpen}
                            className={classNames(classes.menuButton, open && classes.hide)}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="title" color="inherit" noWrap={true} className={classes.title}>
                            {strings.title}
                        </Typography>
                        <Button color="inherit" onClick={this.handleLogOutClick}>{strings.logOutButton}</Button>
                    </Toolbar>
                </AppBar>
                <Drawer
                    variant="permanent"
                    classes={{
                        paper: classNames(classes.drawerPaper, !open && classes.drawerPaperClose),
                    }}
                    open={open}
                >
                    <Grid spacing={0} container={true} className={classes.drawerToolbar} direction="column" justify="space-between">
                        <IconButton onClick={this.handleDrawerClose} style={{ margin: 'auto 0 auto auto' }}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <Divider />
                    </Grid>
                    <div className={classes.toolbar} />

                    <MenuList>
                        {this.menuItems.map((item, index) =>
                            <MenuItem button={true} key={index} selected={currentMenuItem === item.id} onClick={() => this.handleMenuItemChange(item.id)}>
                                <ListItemIcon>
                                    <ListIcon />
                                </ListItemIcon>
                                <ListItemText primary={item.title} />
                            </MenuItem>
                        )}
                    </MenuList>
                    <Divider />
                </Drawer>
                <main className={classes.content}>
                    <div className={classes.toolbar} />
                    {currentMenuItem === MenuItemType.Wineries && <WineriesPage />}
                    {currentMenuItem === MenuItemType.Appellations && <AppellationsPage />}
                    {currentMenuItem === MenuItemType.Classifications && <ClassificationsPage />}
                    {currentMenuItem === MenuItemType.RedVarieties && <RedVarietiesPage />}
                    {currentMenuItem === MenuItemType.WhiteVarieties && <WhiteVarietiesPage />}
                    {currentMenuItem === MenuItemType.Subregions && <SubregionsPage />}
                    {currentMenuItem === MenuItemType.Regions && <RegionsPage />}
                    {currentMenuItem === MenuItemType.Countries && <CountriesPage />}
                    {currentMenuItem === MenuItemType.Continents && <ContinentsPage />}
                </main>
            </div>
        );
    }

    private handleDrawerOpen = () => {
        this.setState({ open: true });
    };

    private handleDrawerClose = () => {
        this.setState({ open: false });
    };

    private handleLogOutClick = () => {
        this.props.logout();
    }

    private handleMenuItemChange = (item: MenuItemType) => {
        this.props.changeMenuItem(item);
    }
}

//#region Connect
interface DispatchAsyncActionCreators extends ActionCreatorsMapObject {
    logout: AccountLogoutAsyncActionCreator;
    changeMenuItem: (current: MenuItemType) => ChangedAction;
}

interface DispatchActionCreators extends ActionCreatorsMapObject {
    logout: AccountLogoutActionCreator;
    changeMenuItem: (current: MenuItemType) => ChangedAction;
}

const mapStateToProps = (state: StoreState): HomePageStateProps => ({
    currentMenuItem: state.currentMenuItem
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => bindActionCreators<DispatchAsyncActionCreators, DispatchActionCreators>(
    {
        logout: accountLogoutAsync,
        changeMenuItem: changed
    },
    dispatch);

export default connect<HomePageStateProps, HomePageDispatchProps>(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)<HomePageProps>(HomePage));
//#endregion



// WEBPACK FOOTER //
// ./src/components/pages/HomePage.tsx